import axiosInstance from "./axiosInstance";
import { saveTokens, getRefreshToken, clearTokens } from "../../utils/storage";

// -------------------- AUTH --------------------

export const login = async (email, password) => {
  try {
    const response = await axiosInstance.post(
      "/auth/login",
      { email, password },
      { headers: { authType: "PreLogin" } }
    );
    const { token, refreshToken } = response.data;
    if (!token || !refreshToken) {
      throw new Error("Invalid response: Missing token or refreshToken");
    }
    saveTokens({ accessToken: token, refreshToken });
    return response.data;
  } catch (error) {
    console.error("Login error:", {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data,
    });
    throw new Error(error.response?.data?.message || "Login failed. Please check your credentials.");
  }
};

export const refreshToken = async () => {
  try {
    const refreshToken = getRefreshToken();
    if (!refreshToken) throw new Error("No refresh token available");

    const response = await axiosInstance.post(
      "/auth/refresh",
      { token: refreshToken },
      { headers: { authType: "PreLogin" } }
    );
    const { token: newAccessToken, refreshToken: newRefreshToken } = response.data;
    saveTokens({ accessToken: newAccessToken, refreshToken: newRefreshToken });
    return response.data;
  } catch (error) {
    clearTokens();
    throw new Error(error.response?.data?.message || "Token refresh failed");
  }
};

export const logout = async () => {
  try {
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      await axiosInstance.post(
        "/auth/logout",
        { token: refreshToken },
        { headers: { authType: "PostLogin" } }
      );
    }
    clearTokens();
    return { message: "Logout successful" };
  } catch (error) {
    console.error("Logout error:", error.message);
    clearTokens();
    throw new Error(error.response?.data?.message || "Logout failed");
  }
};

export const verifyAuth = async () => {
  try {
    const response = await axiosInstance.get("/auth/verify", {
      headers: { authType: "PostLogin" },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Verification failed");
  }
};

// -------------------- CLIENT LOGO --------------------

export const getClientLogo = async () => {
  try {
    const response = await axiosInstance.get("/clientlogo", {
      headers: { authType: "PreLogin" },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to fetch client logo");
  }
};

export const getClientLogoById = async (id) => {
  try {
    const response = await axiosInstance.get(`/clientlogo/${id}`, {
      headers: { authType: "PostLogin" },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to fetch client logo");
  }
};

export const createClientLogo = async (formData) => {
  try {
    const response = await axiosInstance.post("/clientlogo", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        authType: "PostLogin",
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to create client logo");
  }
};

export const updateClientLogo = async (id, formData) => {
  try {
    const response = await axiosInstance.put(`/clientlogo/${id}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        authType: "PostLogin",
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to update client logo");
  }
};

export const deleteClientLogo = async (id) => {
  try {
    const response = await axiosInstance.delete(`/clientLogodel/${id}`, {
      headers: { authType: "PostLogin" },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to delete client logo");
  }
};

// -------------------- TESTIMONIALS --------------------

export const getTestimonials = async () => {
  try {
    const response = await axiosInstance.get("/public/testimonial", {
      headers: { authType: "PreLogin" },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to fetch testimonials");
  }
};

export const getTestimonialById = async (id) => {
  try {
    const response = await axiosInstance.get(`/testimonialfetch/${id}`, {
      headers: { authType: "PostLogin" },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to fetch testimonial");
  }
};

export const createTestimonial = async (formData) => {
  try {
    const response = await axiosInstance.post("/testimonial", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        authType: "PostLogin",
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to create testimonial");
  }
};

export const updateTestimonial = async (id, formData) => {
  try {
    const response = await axiosInstance.put(`/testimonial/${id}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        authType: "PostLogin",
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to update testimonial");
  }
};

export const deleteTestimonial = async (id) => {
  try {
    const response = await axiosInstance.delete(`/testimonialdel/${id}`, {
      headers: { authType: "PostLogin" },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to delete testimonial");
  }
};

// -------------------- PUBLIC DATA --------------------

export const getPlanDetails = async () => {
  try {
    const response = await axiosInstance.get("/public/plandetails", {
      headers: { authType: "PreLogin" },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to fetch plan details");
  }
};

export const getPlans = async () => {
  try {
    const response = await axiosInstance.get("/public/plan", {
      headers: { authType: "PreLogin" },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to fetch plans");
  }
};

export const getTeamMembers = async () => {
  try {
    const response = await axiosInstance.get("/public/teammember", {
      headers: { authType: "PreLogin" },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to fetch team members");
  }
};

export const getPortfolio = async () => {
  try {
    const response = await axiosInstance.get("/public/portfolio", {
      headers: { authType: "PreLogin" },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to fetch portfolio");
  }
};

export const getBlogs = async () => {
  try {
    const response = await axiosInstance.get("/public/blog", {
      headers: { authType: "PreLogin" },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to fetch blogs");
  }
};

// -------------------- SLIDERS --------------------

export const getSliders = async () => {
  try {
    const response = await axiosInstance.get("/public/slider", {
      headers: { authType: "PreLogin" },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to fetch sliders");
  }
};

export const createSlider = async (formData) => {
  try {
    const response = await axiosInstance.post("/slider", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        authType: "PostLogin",
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to create slider");
  }
};

export const updateSlider = async (id, formData) => {
  try {
    const response = await axiosInstance.put(`/slider/${id}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        authType: "PostLogin",
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to update slider");
  }
};

export const deleteSlider = async (id) => {
  try {
    const response = await axiosInstance.delete(`/sliderdel/${id}`, {
      headers: { authType: "PostLogin" },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to delete slider");
  }
};

export const getSliderById = async (id) => {
  try {
    const response = await axiosInstance.get(`/sliderfetch/${id}`, {
      headers: { authType: "PostLogin" },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to fetch slider");
  }
};

// -------------------- ENQUIRY --------------------

export const createEnquiry = async (formData) => {
  try {
    const response = await axiosInstance.post("/public/enquiry", formData, {
      headers: { authType: "PreLogin" },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to create enquiry");
  }
};
export const getAllEnquiries = async () => {
  try {
    const response = await axiosInstance.get("/enquiry", {
      headers: { authType: "PostLogin" },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to fetch enquiries");
  }
};